import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { StyledSelect } from 'Components/Inputs/StyledSelect'
import { PolicyPeriodDuration } from 'Utils/types/benefitProgramPolicy'
import { FlexColumn, spacingXXS } from 'Utils/styles/spacing'

import { getSelectOptions } from 'Utils/helpers/dateSelection'
import { parseServerDate } from 'Utils/helpers/dateUtils'

const DateSelectorContainer = styled(FlexColumn)`
  width: 100%;
  gap: ${spacingXXS};
`

const INDEFINITE = 'indefinite'

type BenefitProgramEndDateSelectorProps = {
  policyDuration: PolicyPeriodDuration
  annualResetMonth?: number
  allowIndefinite?: boolean
  after?: Date
  onChange: (date?: Date) => void
}

export const BenefitProgramEndDateSelector = ({
  policyDuration,
  annualResetMonth,
  allowIndefinite = false,
  after,
  onChange
}: BenefitProgramEndDateSelectorProps) => {
  const placeholderText =
    policyDuration === PolicyPeriodDuration.Quarterly
      ? 'Select a quarter'
      : policyDuration === PolicyPeriodDuration.Yearly
      ? 'Select a year'
      : 'Select a month'

  const updateSelectedEndDate = (selectedOption: string) => {
    onChange(selectedOption === INDEFINITE ? undefined : parseServerDate(selectedOption))
  }

  const selectOptions = useMemo(() => {
    const dateOptions = getSelectOptions(policyDuration, { start: false, annualResetMonth, after })

    if (!allowIndefinite) return dateOptions

    return [{ value: INDEFINITE, label: 'Indefinite' }, ...dateOptions]
  }, [policyDuration, annualResetMonth, allowIndefinite, after])

  return (
    <DateSelectorContainer>
      <FormattedMessage id="createBenefitProgram.scheduledEndDate.label" />
      <StyledSelect
        options={selectOptions}
        handleChange={updateSelectedEndDate}
        defaultValue={allowIndefinite ? INDEFINITE : undefined}
        placeholderText={placeholderText}
      />
    </DateSelectorContainer>
  )
}
